import { useQuery } from '@tanstack/react-query'
import { Eye, MousePointer, Percent, TrendingUp, TrendingDown, Hash } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface GscSummaryCardsProps {
  days: number
}

export default function GscSummaryCards({ days }: GscSummaryCardsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['gsc-summary', days],
    queryFn: () => seoAnalytics.getGscSummary(days),
  })

  const summary = data?.data

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="card p-4 animate-pulse">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-2" />
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-24" />
          </div>
        ))}
      </div>
    )
  }

  const totals = summary?.totals || {}
  const trend = summary?.trend || {}
  const positionDist = summary?.position_distribution || {}

  const trendDirection = trend.change_percent > 0 ? 'up' : trend.change_percent < 0 ? 'down' : 'neutral'
  const TrendIcon = trendDirection === 'up' ? TrendingUp : TrendingDown

  return (
    <div className="space-y-4">
      {/* Main metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-1">
            <Eye className="w-4 h-4" />
            <span className="text-sm">Impresiones</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {(totals.impressions || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">Apariciones en busquedas</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
            <MousePointer className="w-4 h-4" />
            <span className="text-sm">Clics</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {(totals.clicks || 0).toLocaleString()}
          </div>
          <div className="flex items-center gap-1 mt-1">
            {trendDirection !== 'neutral' && (
              <TrendIcon className={`w-3 h-3 ${trendDirection === 'up' ? 'text-green-500' : 'text-red-500'}`} />
            )}
            <span className={`text-xs ${
              trendDirection === 'up' ? 'text-green-600' :
              trendDirection === 'down' ? 'text-red-600' : 'text-slate-500'
            }`}>
              {trend.change_percent > 0 ? '+' : ''}{trend.change_percent || 0}% vs semana ant.
            </span>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
            <Percent className="w-4 h-4" />
            <span className="text-sm">CTR medio</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {totals.ctr || 0}%
          </div>
          <div className="text-xs text-slate-500 mt-1">Click-through rate</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Posicion media</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {totals.position?.toFixed(1) || '-'}
          </div>
          <div className="text-xs text-slate-500 mt-1">En resultados de busqueda</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
            <Hash className="w-4 h-4" />
            <span className="text-sm">Keywords</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {(totals.unique_queries || 0).toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">Consultas unicas</div>
        </div>
      </div>

      {/* Position distribution */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          Distribucion por posicion
        </h3>
        <div className="grid grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-lg font-bold text-green-600 dark:text-green-400">
              {positionDist.top_3 || 0}
            </div>
            <div className="text-xs text-slate-500">Top 3</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
              {positionDist.top_10 || 0}
            </div>
            <div className="text-xs text-slate-500">4-10</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-amber-600 dark:text-amber-400">
              {positionDist.page_2 || 0}
            </div>
            <div className="text-xs text-slate-500">11-20</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-slate-500">
              {positionDist.beyond || 0}
            </div>
            <div className="text-xs text-slate-500">&gt;20</div>
          </div>
        </div>
      </div>
    </div>
  )
}
